import type { Immutable } from "immer";
import React from "react";
import { jt, t } from "ttag";

import type { FeaturedAssociateBlock as FeaturedAssociateBlockValue } from "@reactivated";

/**
 * Faithful port of the legacy server-rendered
 * ``cms/blocks/flagship/featured_associate.html`` (FlagshipPage
 * ``FeaturedAssociateBlock``).
 *
 * Pure value block: ``name`` / ``position`` are plain text, ``experience``
 * renders the stored CHOICE VALUE (e.g. ``"7"``), not its label, exactly as
 * ``{{ value.experience }}`` did. ``quote`` is a RichTextBlock rendered with
 * ``|safe`` (the expanded HTML, NO ``.rich-text`` wrapper), so it is injected
 * raw via ``dangerouslySetInnerHTML``.
 *
 * The legacy ``{% wagtail_image value.photo %}`` had NO specs, so it rendered
 * the ORIGINAL image (``class="slideshow__thumb-nav-image"``,
 * ``alt=_("Associate")``); the eager ``<img src={photo.url}>`` here matches
 * (the accepted eager / no-srcset convention).
 *
 * The "Featured Associate" headline is hard-coded (untranslated) in the legacy
 * template, so it stays a literal string here.
 */
export const FlagshipFeaturedAssociateBlock = (
    props: Immutable<{ value: FeaturedAssociateBlockValue }>,
): React.JSX.Element => {
    const v = props.value;
    // jt preserves the inline <sup> markup of the legacy ``{% blocktrans %}``.
    const reg = <sup key="reg">&reg;</sup>;
    return (
        <div className="associate">
            <div className="lead-in">
                <h1 className="lead-in__headline">Featured Associate</h1>
            </div>
            <div className="associate__background"></div>
            <div className="associate__profile l-capped-width">
                <div className="associate__photo">
                    <figure className="associate__image">
                        {v.photo ? (
                            <img
                                className="slideshow__thumb-nav-image"
                                src={v.photo.url}
                                alt={t`Associate`}
                                loading="lazy"
                                decoding="async"
                            />
                        ) : null}
                    </figure>
                    <div className="associate__caption">
                        <span className="associate__name">{v.name}</span>
                        <span className="associate__title">{v.position}</span>
                    </div>
                </div>
                <div className="associate__info">
                    <div
                        className="associate__quote"
                        dangerouslySetInnerHTML={{ __html: v.quote ?? "" }}
                    />
                    <div className="associate__experience">
                        <p className="associate__helping">
                            {jt`Helping customers with Tempur-Pedic${reg} products for:`}
                        </p>
                        <p className="associate__years">
                            <span className="associate__year">
                                {v.experience}
                            </span>
                            <span className="associate__time">{t`years`}</span>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};
